import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { TrendingUp, AlertTriangle, PhoneOff, FileQuestion, ClipboardCheck, FileUp, Search, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

function AssessmentPreparing({ tier, firmName }) {
  const label = tier === "expanded_assessment" ? "Expanded Assessment"
    : tier === "standard_assessment" ? "Standard Assessment" : "Marketing Performance Assessment";
  const steps = [
    { icon: ClipboardCheck, t: "Engagement confirmed", d: "Your assessment purchase is confirmed and your secure workspace is ready.", done: true },
    { icon: FileUp, t: "Share your data", d: "Provide your marketing invoices, vendor reports, lead records and retained-case data.", active: true },
    { icon: Search, t: "Reconcile & analyze", d: "RetainProof reconciles spend, vendor activity and outcomes, and flags gaps and discrepancies." },
    { icon: FileText, t: "Executive findings", d: "You receive an independent executive findings report and a review consultation." },
  ];
  return (
    <div data-testid="assessment-preparing">
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">{label}</p>
        <h1 className="font-display text-4xl font-light mt-1">Your assessment is being prepared</h1>
        <p className="text-zinc-600 mt-3 max-w-2xl leading-relaxed">
          Welcome{firmName ? `, ${firmName}` : ""}. This is your private RetainProof workspace. Your engagement is a one-time
          Marketing Performance Assessment — there's nothing to configure. Our team reconciles your marketing spend, vendor
          reporting and retained-case outcomes, then delivers an independent executive findings report.
        </p>
      </div>

      <div className="border border-zinc-200 bg-white p-6 sm:p-8" data-testid="assessment-timeline">
        <ol className="relative border-l border-zinc-200 ml-3">
          {steps.map((s, i) => (
            <li key={s.t} className="mb-8 last:mb-0 ml-6" data-testid={`assessment-step-${i}`}>
              <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ${s.done ? "bg-[#156F52]" : s.active ? "bg-[#D5A44A]" : "bg-zinc-200"}`}>
                <s.icon className={`h-3.5 w-3.5 ${s.done || s.active ? "text-white" : "text-zinc-500"}`} />
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-lg">{s.t}</h3>
                {s.active && <span className="font-mono-data text-[10px] uppercase tracking-widest bg-[#D5A44A]/15 text-[#8a6a1f] px-2 py-0.5">Next step</span>}
                {s.done && <span className="font-mono-data text-[10px] uppercase tracking-widest bg-[#156F52]/10 text-[#156F52] px-2 py-0.5">Done</span>}
              </div>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Link to="/app/resources" className="border border-zinc-200 bg-white p-5 hover:shadow-sm transition-all" data-testid="assessment-resources-link">
          <FileText className="h-5 w-5 text-[#174A7E]" />
          <p className="font-display text-lg mt-2">Getting started guide</p>
          <p className="text-sm text-zinc-600 mt-1">See exactly what to gather and how the assessment works.</p>
        </Link>
        <a href="mailto:clientcare@retainproofnow.com" className="border border-zinc-200 bg-white p-5 hover:shadow-sm transition-all" data-testid="assessment-contact-link">
          <ClipboardCheck className="h-5 w-5 text-[#0E7C7B]" />
          <p className="font-display text-lg mt-2">Talk to your analyst</p>
          <p className="text-sm text-zinc-600 mt-1">Questions about your engagement? Reach us at clientcare@retainproofnow.com.</p>
        </a>
      </div>
    </div>
  );
}

const CHART_COLORS = ["#174A7E", "#0E7C7B", "#156F52", "#D5A44A", "#6B4FA3", "#5E6B78", "#B54747"];

function Kpi({ label, value, sub, accent, tid }) {
  return (
    <div
      className="border border-zinc-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
      data-testid={tid}
    >
      <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`font-display text-3xl mt-2 ${accent || ""}`}>{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const { user } = useAuth();
  const preparing = user?.engagement === "assessment" && user?.assessment_status === "preparing";

  useEffect(() => {
    if (preparing) return;
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/vendors/renewals").then((r) => setRenewals(r.data));
  }, [preparing]);

  if (preparing) return <AssessmentPreparing tier={user?.assessment_tier} firmName={user?.firm_name} />;

  if (!data)
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );

  const k = data.kpis;
  const funnelData = [
    { stage: "Inquiries", value: data.funnel.inquiry },
    { stage: "Qualified", value: data.funnel.qualified },
    { stage: "Consultations", value: data.funnel.consultation },
    { stage: "Signed", value: data.funnel.signed },
  ];
  const channelData = data.channels.map((c) => ({ name: c.channel, value: c.revenue || 0 }));

  return (
    <div>
      <OnboardingChecklist />
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Managing partner view</p>
        <h1 className="font-display text-4xl font-light mt-1">Marketing Control Center</h1>
      </div>

      {/* North star + KPIs */}
      <div className="grid lg:grid-cols-4 gap-4 mb-4">
        <div className="lg:col-span-1 border border-[#0B1F33] bg-[#0B1F33] text-white p-6" data-testid="kpi-cost-per-case">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-400">
            North star · Cost per retained case
          </p>
          <p className="font-display text-5xl mt-3">{currency(k.cost_per_case)}</p>
          <p className="text-sm text-zinc-400 mt-2">{k.signed_cases} signed cases this period</p>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-4">
          <Kpi tid="kpi-budgeted" label="Budgeted" value={currency(k.budgeted)} />
          <Kpi tid="kpi-contracted" label="Contracted" value={currency(k.contracted)} />
          <Kpi tid="kpi-invoiced" label="Invoiced" value={currency(k.invoiced)} />
          <Kpi tid="kpi-paid" label="Paid" value={currency(k.paid)} />
          <Kpi
            tid="kpi-remaining"
            label="Budget remaining"
            value={currency(k.remaining)}
            accent={k.remaining < 0 ? "text-[#B54747]" : "text-[#156F52]"}
          />
          <Kpi
            tid="kpi-roi"
            label="Marketing ROI"
            value={`${k.roi}%`}
            accent={k.roi >= 0 ? "text-[#156F52]" : "text-[#B54747]"}
            sub={`${currency(k.revenue)} attributed revenue`}
          />
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 border border-zinc-200 bg-white p-6" data-testid="funnel-chart">
          <h3 className="font-display text-2xl mb-1">Lead-to-Case Funnel</h3>
          <p className="text-xs text-zinc-500 mb-4">Where prospects fall out on the way to a signed case.</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="stage" tick={{ fontSize: 12 }} width={100} />
              <Tooltip cursor={{ fill: "#f4f4f5" }} />
              <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                {funnelData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-zinc-200 bg-white p-6" data-testid="channel-chart">
          <h3 className="font-display text-2xl mb-1">Revenue by Channel</h3>
          <p className="text-xs text-zinc-500 mb-4">Attributed collected/estimated fees.</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {channelData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => currency(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Renewals + intake diagnostics */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-zinc-200 bg-white p-6" data-testid="renewal-alerts">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-[#D5A44A]" />
            <h3 className="font-display text-2xl">Contract Alerts</h3>
          </div>
          {renewals.length === 0 && <p className="text-sm text-zinc-500">No upcoming renewal or cancellation deadlines.</p>}
          <div className="space-y-2">
            {renewals.map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between border border-zinc-100 bg-zinc-50 px-4 py-3"
                data-testid={`renewal-alert-${i}`}
              >
                <div>
                  <p className="text-sm font-medium">{r.vendor_name}</p>
                  <p className="font-mono-data text-xs text-zinc-500">{r.type}</p>
                </div>
                <span
                  className={`font-mono-data text-xs px-2 py-1 ${
                    r.days_left <= 15 ? "bg-[#B54747] text-white" : "bg-[#D5A44A] text-white"
                  }`}
                >
                  {r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue` : `${r.days_left}d left`}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-zinc-200 bg-white p-6" data-testid="intake-diagnostics">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-[#174A7E]" />
            <h3 className="font-display text-2xl">Failure Diagnostics</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 border border-zinc-100 bg-zinc-50 px-4 py-3">
              <PhoneOff className="h-5 w-5 text-[#B54747]" />
              <div>
                <p className="text-sm">
                  <span className="font-display text-2xl mr-2">{data.intake.good_not_contacted}</span>
                  qualified leads not contacted within 15 minutes
                </p>
                <p className="text-xs text-zinc-500">Intake failure — good leads dropped by the team.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 border border-zinc-100 bg-zinc-50 px-4 py-3">
              <FileQuestion className="h-5 w-5 text-[#D5A44A]" />
              <div>
                <p className="text-sm">
                  <span className="font-display text-2xl mr-2">{data.intake.missing_source}</span>
                  leads with no recorded source
                </p>
                <p className="text-xs text-zinc-500">Operational failure — attribution is broken.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
